import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { assignmentActions } from '@nxsys/store/slices/assignment/reducer/assignmentSlice';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { Space, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import AssignmentFormStacked, {
  AssignmentFormRef,
  AssignmentFormValues,
} from '../../asssignment/components/AssignmentFormStacked';

const AgencyAssignmentCreate = ({
  agency,
  onBack,
  onDone,
  initialValues,
}: {
  agency: IAgency;
  onBack: () => void;
  onDone: () => void;
  initialValues?: Partial<AssignmentFormValues>;
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const formRef = useRef<AssignmentFormRef>(null);
  const [isCreatingAssignment, setIsCreatingAssignment] = useState(false);

  const defaultValues = useMemo(() => {
    return {
      ...(initialValues ?? {}),
      agencyName: { label: agency?.name ?? '', value: agency?.id ?? '' },
    };
  }, [agency?.id, agency?.name, initialValues]);

  const handleSubmit = async (values: AssignmentFormValues) => {
    try {
      setIsCreatingAssignment(true);
      const params: any = {
        agencyRef: agency?.id ?? values.agencyName?.value,
        assignmentName: values.assignmentName ?? '',
        clientRef: values.clientRef ?? '',
        endDate: values.endDate ? moment(values.endDate).toISOString() : null,
        invoicingAddress: {
          address: [
            values.invoicingAddress?.address1 ?? '',
            values.invoicingAddress?.address2 ?? '',
            values.invoicingAddress?.address3 ?? '',
          ],
          city: values.invoicingAddress?.city ?? '',
          county: values.invoicingAddress?.county ?? '',
          postCode: values.invoicingAddress?.postCode ?? '',
          country: values.invoicingAddress?.country?.value ?? '',
        },
        projectRef: values.projectRef ?? '',
        rates: values.rates?.map((rate) => ({
          ...(rate as any).rateCode?.fullData,
          rateCode: (rate.rateCode as any)?.value,
          description: rate.description,
          rate: rate.rate,
          rateType: rate.rateType,
          vatRate: rate.vatRate,
          vatDetails: (rate.vatDetails as any)?.label,
        })),
        siteId: values.siteId?.value || null,
        startDate: values.startDate
          ? moment(values.startDate).toISOString()
          : '',
        status: (values.status as any)?.value ?? '',
        subcontractorRef: values.employeeAndSubcontractor?.value ?? '',
        workLocation: {
          address: [
            values.workLocation?.address1 ?? '',
            values.workLocation?.address2 ?? '',
            values.workLocation?.address3 ?? '',
          ],
          city: values.workLocation?.city ?? '',
          county: values.workLocation?.county ?? '',
          postCode: values.workLocation?.postCode ?? '',
          country: (values.workLocation?.country as any)?.value ?? '',
        },
        file: null,
      };

      if ((values.attachment as File)?.name) {
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: values.attachment as File }),
        ).unwrap();
        params.file = res.url;
      }

      await dispatch(assignmentActions.createAssignment(params)).unwrap();
      onDone();
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreatingAssignment(false);
    }
  };

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center justify-between gap-4 w-full">
        <div className="flex items-center gap-4 min-w-0">
          <AppBackButton onClick={onBack} />
          <Typography.Text className="font-medium text-gray-800 text-2xl truncate">
            {messages['assignment.addNew'] ?? 'Add New Assignment'}
          </Typography.Text>
        </div>
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel'] || 'Cancel'}
            onClick={onBack}
            disabled={isCreatingAssignment}
          />
          <AppButton
            type="primary"
            name={messages['common.save'] || 'Save'}
            onClick={() => formRef.current?.submit()}
            loading={isCreatingAssignment}
          />
        </Space>
      </div>
    );
  }, [isCreatingAssignment, messages, onBack]);

  return (
    <div>
      <div className="px-6 py-4 rounded-lg bg-white mb-2">{renderTitle()}</div>
      <AssignmentFormStacked
        formRef={formRef}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        hideAgencyField
      />
    </div>
  );
};

export default AgencyAssignmentCreate;
